import * as React from 'react';
import styled from 'styled-components';

import Routes from 'routing/Routes';
import { ExternalLink } from 'components/common';
import { DEFAULT_TIMERANGE } from 'views/Constants';
import SearchLink from 'views/logic/search/SearchLink';
import { createElasticsearchQueryString } from 'views/logic/queries/Query';
import { Button } from 'components/bootstrap';
import { useTestNotification, useTriggerSecurityEventNotification } from 'security-app/hooks/useSecurityEventsAPI';
import type { AbsoluteTimeRange } from 'views/logic/queries/Query';
import type { EventProcedureStepAPIType } from 'security-app/hooks/api/eventProceduresAPI.types';
import type { Event } from 'components/events/events/types';

import EventProcedureLinkButton from './EventProcedureLinkButton';

const StyledButton = styled(Button)`
  border-radius: 4px;
  width: 110px;
`;

type Props = {
  step: EventProcedureStepAPIType;
  event?: Event;
};

const GoToDashboardLink = (props) => {
  const { config: dashboardConfig } = props.config;
  const params = props.config.config.parameters;
  const queryString = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  const parameterizedLink = queryString
    ? `${Routes.VIEWS.VIEWID(dashboardConfig.dashboard_id)}?${queryString}`
    : Routes.VIEWS.VIEWID(dashboardConfig.dashboard_id);

  return <EventProcedureLinkButton to={parameterizedLink} title="Go to Dashboard" />;
};

const GoToLink = (props) => {
  const {
    config: { link },
  } = props.config;

  const clickableLink = link.startsWith('http://') || link.startsWith('https://') ? link : `https://${link}`;

  return <ExternalLink href={clickableLink}>{props.config?.title || 'Go to Link'}</ExternalLink>;
};

const SendNotificationButton = (props) => {
  const { config: notificationConfig } = props.config;
  const { triggerSecurityEventNotification, triggeringSecurityEventNotification } =
    useTriggerSecurityEventNotification();
  const { testNotification, testingNotification } = useTestNotification();

  if (!props.event?.id) {
    return (
      <StyledButton
        bsSize="xs"
        onClick={() => testNotification({ id: notificationConfig.notification_id })}
        disabled={testingNotification}>
        Test Notification
      </StyledButton>
    );
  }

  return (
    <StyledButton
      bsSize="xs"
      onClick={() =>
        triggerSecurityEventNotification({ id: props.event?.id, notificationId: notificationConfig.notification_id })
      }
      disabled={triggeringSecurityEventNotification}>
      Send Notification
    </StyledButton>
  );
};

const PerformSearchButton = (props) => {
  const { config: searchConfig } = props.config;
  const { event } = props;

  const buildSavedSearchLink = () => {
    const queryString = Object.entries(searchConfig.parameters)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    const baseLink = Routes.VIEWS.VIEWID(searchConfig.saved_search);
    const timeRangeParams = event?.replay_info
      ? `rangetype=absolute&from=${event.replay_info.timerange_start}&to=${event.replay_info.timerange_end}`
      : '';

    return `${baseLink}?${queryString ? `${queryString}&` : ''}${timeRangeParams}`;
  };

  const buildDynamicSearchLink = () => {
    const searchTimerange: AbsoluteTimeRange = {
      type: 'absolute',
      from: event?.replay_info?.timerange_start,
      to: event?.replay_info?.timerange_end,
    };

    return SearchLink.builder()
      .timerange(event ? searchTimerange : DEFAULT_TIMERANGE)
      .query(createElasticsearchQueryString(searchConfig.query))
      .build()
      .toURL();
  };

  const link = searchConfig.use_saved_search
    ? buildSavedSearchLink()
    : buildDynamicSearchLink();

  return <EventProcedureLinkButton to={link} title="Perform Search" />;
};

function EventProcedureStepActionButton({ step, event = undefined }: Props) {
  if (!step.action) {
    return null;
  }

  const actionToComponentMap = {
    'go_to_dashboard': GoToDashboardLink,
    'link': GoToLink,
    'execute_notification': SendNotificationButton,
    'perform_search': PerformSearchButton,
  };

  const {
    action: { config },
  } = step;
  const Component = actionToComponentMap[config.type];

  return <Component config={step.action} event={event} />;
}

export default EventProcedureStepActionButton;
